/**
 * Reservations (Vormerkungen, docs/18-vorratsbuch.md, V3): stock held for a recipe without changing
 * it. SpeisePlan reserves what a plan's recipes need from the pantry, sends only the rest to the
 * shopping list, and reports „gekocht“, which books the issue. The reason of a reservation is the
 * recipe; several recipes may reserve the same article (Risotto 375 ml and Weinschaumsoße 200 ml of
 * one bottle of Grauburgunder).
 *
 * Rules:
 *   - A reservation is no booking: master data of type `reservation`, one per plan of SpeisePlan
 *     (`plan:<id>`), holding the whole state of that plan; the journal and its properties stay as
 *     they are. A „gekocht“ is recorded under `cooked:<key>`, so it is booked once.
 *   - Available = stock − reservations, per base product and per article; restock counts with it.
 *   - A reservation lapses `days` after its day (2, decided 18.10.2026), without a word to anyone.
 *   - Reserving takes the days in order, then the order asked; the article is the one whose stock
 *     expires first (FEFO) and still has enough unreserved; for wine only one marked „Zum Kochen“.
 *   - Cooking takes the reserved article first, then FEFO over the others; what is not there is not
 *     booked.
 */
import type { Entity } from "./entities.ts";
import { RESERVATION_LAPSE_DAYS, allocate } from "@household-kit/contracts";
import type { StockUnit } from "@household-kit/contracts";

/** Days after the cooking day a reservation still holds (docs/18-vorratsbuch.md: 2; the contract's constant). */
export const RESERVATION_DAYS = RESERVATION_LAPSE_DAYS;

export interface RecipeRef {
  id: string;
  title: string;
}

export interface ReservationLine {
  product: string;
  unit: StockUnit;
  /** As asked. */
  qty: number;
  /** What could be held: at most `qty`. */
  reserved: number;
  recipe: RecipeRef;
  /** The cooking day, „2026-10-24“. */
  day: string;
  /** The article held, by id; for wine the cooking wine. */
  article?: string;
}

export interface PlanReservation {
  plan: string;
  lines: ReservationLine[];
  at: number;
}

/** One account of stock that a product counts: where it lies and when it expires. */
export interface ProductAccount {
  article: string;
  lager: string;
  location: string;
  qty: number;
  bestBefore?: string;
}

export const productKey = (product: string, unit: string) => `${product}|${unit}`;

/** The plan in a master data entity, or null. */
export function reservationOf(e: Entity): PlanReservation | null {
  const f = e.fields;
  if (e.type !== "reservation" || !e.id.startsWith("plan:") || f.archived === true || !Array.isArray(f.lines)) return null;
  const lines = (f.lines as ReservationLine[]).filter((l) => typeof l?.product === "string" && typeof l.qty === "number" && typeof l.reserved === "number" && typeof l.day === "string" && typeof l.recipe?.title === "string");
  return { plan: e.id.slice(5), lines, at: typeof f.at === "number" ? f.at : 0 };
}

/** „2026-10-24“ plus `days`. */
export function addDays(day: string, days: number): string {
  const [y, m, d] = day.split("-").map(Number);
  const t = new Date(Date.UTC(y!, m! - 1, d! + days));
  return t.toISOString().slice(0, 10);
}

/** The reservations that still hold on `today`: not lapsed, with something reserved. */
export function activeLines(plans: Iterable<PlanReservation>, today: string, days = RESERVATION_DAYS): (ReservationLine & { plan: string })[] {
  const out: (ReservationLine & { plan: string })[] = [];
  for (const p of plans) for (const l of p.lines) if (l.reserved > 0 && addDays(l.day, days) >= today) out.push({ ...l, plan: p.plan });
  return out;
}

export function reservedByProduct(lines: Iterable<ReservationLine>): Map<string, number> {
  const out = new Map<string, number>();
  for (const l of lines) out.set(productKey(l.product, l.unit), (out.get(productKey(l.product, l.unit)) ?? 0) + l.reserved);
  return out;
}

export function reservedByArticle(lines: Iterable<ReservationLine>): Map<string, number> {
  const out = new Map<string, number>();
  for (const l of lines) if (l.article) out.set(l.article, (out.get(l.article) ?? 0) + l.reserved);
  return out;
}

/** Articles of a product in FEFO order (the earliest best-before of each first, without one last), with their stock. */
function articlesOf(accounts: readonly ProductAccount[]): { article: string; qty: number }[] {
  const by = new Map<string, { qty: number; first: string }>();
  for (const a of accounts) {
    const known = by.get(a.article) ?? { qty: 0, first: "9999-99-99" };
    known.qty += a.qty;
    if (a.bestBefore && a.bestBefore < known.first) known.first = a.bestBefore;
    by.set(a.article, known);
  }
  return [...by.entries()].sort((x, y) => x[1].first.localeCompare(y[1].first) || x[0].localeCompare(y[0])).map(([article, v]) => ({ article, qty: v.qty }));
}

export interface ReserveRequest {
  product: string;
  unit: StockUnit;
  qty: number;
  recipe: RecipeRef;
  day: string;
}

/**
 * The lines of a plan reserved from what is available besides the other plans' reservations
 * (`others`): the amounts as the contract's `allocate` shares them out (days in order, then as
 * asked), so SpeisePlan and VorratsBuch count alike; the article chosen here. In the order of the
 * request.
 */
export function reserve(requests: readonly ReserveRequest[], stock: ReadonlyMap<string, readonly ProductAccount[]>, others: readonly ReservationLine[]): ReservationLine[] {
  const byProduct = reservedByProduct(others);
  const byArticle = reservedByArticle(others);
  const amounts = allocate(requests, (product, unit) => (stock.get(productKey(product, unit)) ?? []).reduce((s, a) => s + a.qty, 0) - (byProduct.get(productKey(product, unit)) ?? 0));
  const order = requests.map((r, i) => ({ r, i })).sort((a, b) => a.r.day.localeCompare(b.r.day) || a.i - b.i);
  const out: ReservationLine[] = new Array(requests.length);
  for (const { r, i } of order) {
    const accounts = stock.get(productKey(r.product, r.unit)) ?? [];
    const reserved = amounts[i]!;
    let article: string | undefined;
    if (reserved > 0) {
      const free = articlesOf(accounts).map((a) => ({ ...a, free: a.qty - (byArticle.get(a.article) ?? 0) }));
      article = (free.find((a) => a.free >= reserved) ?? free.find((a) => a.free > 0))?.article;
      if (article) byArticle.set(article, (byArticle.get(article) ?? 0) + reserved);
    }
    out[i] = { product: r.product, unit: r.unit, qty: r.qty, reserved, recipe: r.recipe, day: r.day, ...(article ? { article } : {}) };
  }
  return out;
}

export interface Take {
  article: string;
  lager: string;
  location: string;
  qty: number;
}

/**
 * What cooking takes for one item: the preferred article first (the one reserved for the recipe),
 * then the others, each by its best-before; one issue per article and location.
 */
export function cookTakes(qty: number, accounts: readonly ProductAccount[], preferred?: string): { taken: number; takes: Take[] } {
  const sorted = [...accounts].sort((a, b) => Number(b.article === preferred) - Number(a.article === preferred) || (a.bestBefore ?? "9999-99-99").localeCompare(b.bestBefore ?? "9999-99-99"));
  const takes = new Map<string, Take>();
  let left = qty;
  for (const a of sorted) {
    if (left <= 0) break;
    const q = Math.min(left, a.qty);
    if (q <= 0) continue;
    left -= q;
    const key = `${a.article}|${a.location}`;
    const t = takes.get(key) ?? { article: a.article, lager: a.lager, location: a.location, qty: 0 };
    t.qty += q;
    takes.set(key, t);
  }
  return { taken: qty - left, takes: [...takes.values()] };
}
